import { defineComponent, PropType } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapState } from "pinia";

import { useHospiceConvertToPending } from "../../../store/useHospiceConvertToPending";
import { IEnumResponse } from "../../../../../../resources-vue/vue-interfaces/i-enum";
import { IGetSupplyByName, IPatientSupply } from "../../../../../../resources-vue/vue-interfaces/i-get-supply";
import { ITypeaheadOptions } from "../../../../../../resources-vue/vue-interfaces/i-typeahead";
import { SupplyService } from "../../../../../../services/supply.service";
import { ToastrService } from "../../../../../../services/toastr.service";
import { EnumsService } from "../../../../../../services/enums.service";

import FormSection from "../../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import TypeaheadInput from "../../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import TableEmpty from "../../../../../../resources-vue/vue-custom-elements/TableEmpty/TableEmpty.vue";
import SupplyTableRow from "./SupplyTableRow.vue";

export default defineComponent({
    components: {
        FormSection,
        TypeaheadInput,
        TableEmpty,
        SupplyTableRow
    },
    props: {
        orderId: {
            type: String,
            default: null
        },
        isReferral: {
            type: Boolean,
            default: false
        },
        patientStatus: {
            type: Number,
            default: null
        },
        providerId: {
            type: String,
            default: ""
        },
        supplies: { type: Array as PropType<IPatientSupply[]>, default: [] }
    },
    data() {
        return {
            columns: 7,
            patientStatusEnums: [] as IEnumResponse[],
            supplyList: [] as IGetSupplyByName[],
            selectionSupply: null as ITypeaheadOptions,
            savetoAdd: null as IPatientSupply,
            showSupplyInputRow: null as boolean,
            _supplyService: null,
            _toastrService: null,
            _enumsService: null
        };
    },
    computed: {
        ...mapState(useHospiceConvertToPending, ["accountId", "application"]),
        isEditAllowed() {
            let pendingStatus = this.patientStatusEnums.find((status) => status.name.toLowerCase().includes("pending"));
            return this.orderId || this.isReferral || (pendingStatus && this.patientStatus == pendingStatus.value);
        }
    },
    watch: {
        selectionSupply(newValue: ITypeaheadOptions) {
            let supplySelected: IGetSupplyByName;
            if (newValue) {
                supplySelected = this.supplyList.find((supply) => supply.Id == newValue.value);
                if (supplySelected) {
                    this.savetoAdd = {
                        id: null,
                        supplyId: supplySelected.Id,
                        name: supplySelected.Name,
                        unitCost: supplySelected.UnitCost,
                        isFormulary: supplySelected.IsFormulary,
                        startDate: "",
                        endDate: "",
                        isRelated: null,
                        notRelatedComments: null
                    };
                    this.showSupplyInputRow = true;
                }
            } else {
                this.showSupplyInputRow = false;
            }
        }
    },
    emits: ["update:supplies"],
    created() {
        this._supplyService = Container.instance.get(SupplyService);
        this._toastrService = Container.instance.get(ToastrService);
        this._enumsService = Container.instance.get(EnumsService);
    },
    async mounted() {
        let patientStatusEnums = await this._enumsService.getPatientStatus();
        this.patientStatusEnums = patientStatusEnums.getJson();
    },
    methods: {
        async getSupplyList(filter: string) {
            this.supplyList = await this._supplyService.getSuppliesByName({
                term: filter,
                providerIds: [this.providerId],
                application: this.application,
                accountId: this.accountId
            });
            return this.displayPatientSupply(this.supplyList);
        },
        displayPatientSupply(getSupply: IGetSupplyByName[]): ITypeaheadOptions[] {
            return getSupply.map((value: IGetSupplyByName, index, number) => ({
                value: value.Id,
                name: value.Name
            }));
        },
        selectionSupplyChanged(data: ITypeaheadOptions) {
            this.selectionSupply = data;
        },
        saveNewSupply(newSupply: IPatientSupply, oldSupply: IPatientSupply) {
            let existingSupply = this.supplies.find(
                (supply) =>
                    supply.supplyId == newSupply.supplyId &&
                    supply.startDate == newSupply.startDate &&
                    supply.endDate == newSupply.endDate
            );
            if (existingSupply) {
                this._toastrService.info({
                    title: `${existingSupply.name} already exists`,
                    message: `${existingSupply.name} is already assigned to patient between ${existingSupply.startDate} and ${existingSupply.endDate}.`
                });
            } else {
                this.supplies.push(newSupply);
                this.cancelNewSupply();
            }
        },
        cancelNewSupply() {
            this.savetoAdd = {
                id: null,
                supplyId: null,
                name: null,
                unitCost: null,
                isFormulary: null,
                startDate: "",
                endDate: "",
                isRelated: null,
                notRelatedComments: null
            };
            this.selectionSupply = null;
            this.showSupplyInputRow = false;
        },
        updateSupply(newSupply: IPatientSupply, oldSupply: IPatientSupply) {
            let supplyIndex = this.supplies.findIndex(
                (supply) =>
                    supply.supplyId == oldSupply.supplyId &&
                    supply.startDate == oldSupply.startDate &&
                    supply.endDate == oldSupply.endDate
            );
            if (supplyIndex > -1) {
                this.supplies[supplyIndex] = newSupply;
            }
        },
        deleteSupplyFromList(deleteSupply: IPatientSupply) {
            let supplies = this.supplies.filter(
                (supply) =>
                    !(
                        supply.supplyId == deleteSupply.supplyId &&
                        supply.startDate == deleteSupply.startDate &&
                        supply.endDate == deleteSupply.endDate
                    )
            );
            this.$emit("update:supplies", supplies);
        }
    }
});
